<script setup lang="ts">
import { resolveDynamicComponent, computed } from "vue";
import type { PropType, defineComponent } from "vue";
import type { ApiBlockGridItem } from "../../types";
import UnknownBlock from "./UnknownBlock.vue";
import { getBlockComponentName } from "#imports";

const props = defineProps( {
  items: {
    type: Array as PropType<ApiBlockGridItem[]>,
    default: () => [],
  },
} );

const renderComponents = computed( () => {
  const result: ( string | ReturnType<typeof defineComponent> )[] = [];

  for ( const name of componentNames.value ) {
    if ( isComponent( name ) ) {
      result.push( name );
    } else {
      result.push( UnknownBlock );
    }
  }

  return result;
} );

const componentNames = computed( () => {
  return props.items.map( block => getBlockComponentName( block.content.contentType ) );
} );

function isComponent ( name: any ) {
  const result = typeof resolveDynamicComponent( name ) !== "string";
  return result;
}
</script>

<template>
  <div class="umb-block-grid__layout-container">
    <component
      :is="renderComponents[index]"
      v-for="(item, index) in items"
      :key="index"
      :index="index"
      class="umb-block-grid__layout-item"
      :content="item.content"
      :settings="item.settings"
      :areas="item.areas"
      :data-content-element-type-alias="item.content.contentType"
      :data-col-span="item.columnSpan"
      :data-row-span="item.rowSpan"
      :style="`--umb-block-grid--item-column-span: ${item.columnSpan}; --umb-block-grid--item-row-span: ${item.rowSpan};`" />
  </div>
</template>

<style scoped lang="scss">
.umb-block-grid__layout-container {
  position: relative;
  display: grid;
  grid-template-columns: repeat( var(--umb-block-grid--grid-columns, 1), minmax(0, 1fr) );
  grid-auto-flow: row;
  grid-auto-rows: minmax(50px, min-content);

  column-gap: var(--umb-block-grid--column-gap, 0);
  row-gap: var(--umb-block-grid--row-gap, 0);
}

.umb-block-grid__layout-item {
  position: relative;
  /* For small devices we scale columnSpan by three, to make everything bigger than 1/3 take full width: */
  grid-column-end: span min(calc(var(--umb-block-grid--item-column-span, 1) * 3), var(--umb-block-grid--grid-columns));
  grid-row: span var(--umb-block-grid--item-row-span, 1);
}

@media (min-width: 1024px) {
  .umb-block-grid__layout-item {
    grid-column-end: span min(var(--umb-block-grid--item-column-span, 1), var(--umb-block-grid--grid-columns));
  }
}
</style>
